import {
  Box,
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from '@chakra-ui/react';
import { InternalGetIngestionDocumentFragment } from '@client/graphql/__generated__/types';
import { FC, useMemo } from 'react';

interface FieldValuesComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  contractDocuments: InternalGetIngestionDocumentFragment['contractDocuments'];
}

interface ExtractedFieldComparison {
  fieldKey: string;
  ingestionValue: unknown;
  aiValue: unknown;
}

interface SourceField {
  fieldKey: string;
  label: string;
  value: unknown;
}

interface InterpretedField {
  fieldKey: string;
  value: unknown;
}

interface ContractDocComparison {
  contractDocumentId: string;
  documentName: string;
  extractedFields: ExtractedFieldComparison[];
  sourceFields: SourceField[];
  interpretedFields: InterpretedField[];
}

/**
 * Formats a field value for display in the comparison table.
 */
const formatValue = (value: unknown): string => {
  if (value === null) return '<null>';
  if (value === undefined) return '-';
  if (typeof value === 'string') return value || '<empty>';
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.length === 0 ? '<empty array>' : value.join(', ');
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return String(value);
};

/**
 * Returns the appropriate color for a cell based on whether a value exists.
 */
const getCellColor = (value: unknown): string => {
  if (value === null || value === undefined) {
    return 'compliance.debug.code.text.warning';
  }

  return 'compliance.debug.text';
};

export const FieldValuesComparisonModal: FC<
  FieldValuesComparisonModalProps
> = ({ isOpen, onClose, contractDocuments }) => {
  const comparisonData = useMemo(() => {
    const data: ContractDocComparison[] = [];

    contractDocuments.forEach((contractDoc) => {
      const documentName =
        contractDoc.documentVersion?.document?.name || 'Unnamed Document';

      const ingestionFieldValues =
        (contractDoc.ingestionFieldValues as Record<string, unknown>) ?? {};
      const aiFieldValues =
        (contractDoc.aiIngestionFieldValues as Record<string, unknown>) ?? {};
      const interpretedFieldValues =
        (contractDoc.interpretedFieldValues as Record<string, unknown>) ?? {};

      // Get fields marked as aiInterpret from documentVersionFields
      const aiInterpretFieldKeys = new Set<string>();
      const fieldLabels = new Map<string, string>();

      contractDoc.documentVersion?.documentVersionFields?.forEach((dvf) => {
        if (dvf.field.aiInterpret) {
          aiInterpretFieldKeys.add(dvf.field.mappingKey);
          fieldLabels.set(dvf.field.mappingKey, dvf.field.label);
        }
      });

      // Build extracted fields (ingestion vs AI) - excluding aiInterpret source fields
      const extractedFieldKeys = new Set<string>([
        ...Object.keys(ingestionFieldValues),
        ...Object.keys(aiFieldValues),
      ]);

      const extractedFields: ExtractedFieldComparison[] = Array.from(
        extractedFieldKeys
      )
        .filter((key) => !aiInterpretFieldKeys.has(key))
        .sort()
        .map((fieldKey) => ({
          fieldKey,
          ingestionValue: ingestionFieldValues[fieldKey],
          aiValue: aiFieldValues[fieldKey],
        }));

      // Build source fields (aiInterpret=true fields with their values)
      const sourceFields: SourceField[] = Array.from(aiInterpretFieldKeys)
        .filter(
          (key) =>
            ingestionFieldValues[key] !== undefined ||
            aiFieldValues[key] !== undefined
        )
        .sort()
        .map((fieldKey) => ({
          fieldKey,
          label: fieldLabels.get(fieldKey) || fieldKey,
          value: aiFieldValues[fieldKey] ?? ingestionFieldValues[fieldKey],
        }));

      // Build interpreted fields
      const interpretedFields: InterpretedField[] = Object.entries(
        interpretedFieldValues
      )
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([fieldKey, value]) => ({
          fieldKey,
          value,
        }));

      const hasData =
        extractedFields.length > 0 ||
        sourceFields.length > 0 ||
        interpretedFields.length > 0;

      if (hasData) {
        data.push({
          contractDocumentId: contractDoc.id,
          documentName,
          extractedFields,
          sourceFields,
          interpretedFields,
        });
      }
    });

    return data;
  }, [contractDocuments]);

  const hasAnyData = comparisonData.length > 0;

  return (
    <Modal isOpen={isOpen} scrollBehavior="inside" size="5xl" onClose={onClose}>
      <ModalOverlay />
      <ModalContent bg="compliance.debug.bg" maxH="85vh">
        <ModalHeader color="compliance.debug.text">
          Field Values Comparison
        </ModalHeader>
        <ModalCloseButton color="compliance.debug.text" />
        <ModalBody>
          {!hasAnyData ? (
            <Text
              color="compliance.text.muted"
              fontSize="sm"
              fontStyle="italic"
            >
              No field values available for comparison
            </Text>
          ) : (
            <VStack align="stretch" spacing={6}>
              {comparisonData.map((docComparison) => (
                <Box key={docComparison.contractDocumentId}>
                  <Text
                    color="compliance.debug.text"
                    fontSize="sm"
                    fontWeight="semibold"
                    mb={3}
                  >
                    {docComparison.documentName}
                  </Text>

                  <VStack align="stretch" spacing={4}>
                    {/* Table 1: Extracted Field Values */}
                    {docComparison.extractedFields.length > 0 && (
                      <Box>
                        <Text
                          color="compliance.debug.text"
                          fontSize="xs"
                          fontWeight="medium"
                          mb={2}
                          textTransform="uppercase"
                        >
                          Extracted Field Values
                        </Text>
                        <Box
                          borderColor="compliance.debug.border"
                          borderRadius="md"
                          borderWidth={1}
                          maxH="350px"
                          overflowY="auto"
                        >
                          <Table size="sm" variant="simple">
                            <Thead position="sticky" top={0} zIndex={1}>
                              <Tr>
                                <Th
                                  bg="compliance.debug.code.bg"
                                  color="compliance.debug.text"
                                  fontSize="xs"
                                  minW="200px"
                                >
                                  Field Key
                                </Th>
                                <Th
                                  bg="compliance.debug.code.bg"
                                  color="compliance.debug.text"
                                  fontSize="xs"
                                  minW="180px"
                                >
                                  Ingestion
                                </Th>
                                <Th
                                  bg="compliance.debug.code.bg"
                                  color="compliance.debug.text"
                                  fontSize="xs"
                                  minW="180px"
                                >
                                  AI Ingestion
                                </Th>
                              </Tr>
                            </Thead>
                            <Tbody>
                              {docComparison.extractedFields.map((field) => (
                                <Tr key={field.fieldKey}>
                                  <Td
                                    color="compliance.debug.text"
                                    fontSize="xs"
                                    fontWeight="medium"
                                  >
                                    {field.fieldKey}
                                  </Td>
                                  <Td>
                                    <Tooltip
                                      hasArrow
                                      bg="compliance.tooltip.bg"
                                      color="compliance.tooltip.text"
                                      label={formatValue(field.ingestionValue)}
                                      placement="top"
                                    >
                                      <Text
                                        color={getCellColor(
                                          field.ingestionValue
                                        )}
                                        fontSize="xs"
                                        maxW="180px"
                                        noOfLines={1}
                                      >
                                        {formatValue(field.ingestionValue)}
                                      </Text>
                                    </Tooltip>
                                  </Td>
                                  <Td>
                                    <Tooltip
                                      hasArrow
                                      bg="compliance.tooltip.bg"
                                      color="compliance.tooltip.text"
                                      label={formatValue(field.aiValue)}
                                      placement="top"
                                    >
                                      <Text
                                        color={getCellColor(field.aiValue)}
                                        fontSize="xs"
                                        maxW="180px"
                                        noOfLines={1}
                                      >
                                        {formatValue(field.aiValue)}
                                      </Text>
                                    </Tooltip>
                                  </Td>
                                </Tr>
                              ))}
                            </Tbody>
                          </Table>
                        </Box>
                      </Box>
                    )}

                    {/* Table 2: AI Interpretation */}
                    {(docComparison.sourceFields.length > 0 ||
                      docComparison.interpretedFields.length > 0) && (
                      <Box>
                        <Text
                          color="compliance.debug.text"
                          fontSize="xs"
                          fontWeight="medium"
                          mb={2}
                          textTransform="uppercase"
                        >
                          AI Interpretation
                        </Text>
                        <Box
                          borderColor="compliance.debug.border"
                          borderRadius="md"
                          borderWidth={1}
                          p={3}
                        >
                          <HStack align="flex-start" spacing={4}>
                            {/* Source Fields */}
                            {docComparison.sourceFields.length > 0 && (
                              <Box flex={1}>
                                <Text
                                  color="compliance.debug.text"
                                  fontSize="xs"
                                  fontWeight="medium"
                                  mb={2}
                                  opacity={0.7}
                                >
                                  Source Fields (aiInterpret)
                                </Text>
                                <Box maxH="200px" overflowY="auto" pr={2}>
                                  <Table size="sm" variant="simple">
                                    <Thead position="sticky" top={0} zIndex={1}>
                                      <Tr>
                                        <Th
                                          bg="compliance.debug.code.bg"
                                          color="compliance.debug.text"
                                          fontSize="xs"
                                          p={2}
                                        >
                                          Field Key
                                        </Th>
                                        <Th
                                          bg="compliance.debug.code.bg"
                                          color="compliance.debug.text"
                                          fontSize="xs"
                                          p={2}
                                        >
                                          Value
                                        </Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {docComparison.sourceFields.map(
                                        (field) => (
                                          <Tr key={field.fieldKey}>
                                            <Td fontSize="xs" p={2}>
                                              <Tooltip
                                                hasArrow
                                                bg="compliance.tooltip.bg"
                                                color="compliance.tooltip.text"
                                                label={field.fieldKey}
                                                placement="top"
                                              >
                                                <Text
                                                  color="compliance.debug.text"
                                                  fontWeight="medium"
                                                  maxW="150px"
                                                  noOfLines={1}
                                                >
                                                  {field.fieldKey}
                                                </Text>
                                              </Tooltip>
                                            </Td>
                                            <Td fontSize="xs" p={2}>
                                              <Tooltip
                                                hasArrow
                                                bg="compliance.tooltip.bg"
                                                color="compliance.tooltip.text"
                                                label={formatValue(field.value)}
                                                placement="top"
                                              >
                                                <Text
                                                  color={getCellColor(
                                                    field.value
                                                  )}
                                                  maxW="200px"
                                                  noOfLines={2}
                                                >
                                                  {formatValue(field.value)}
                                                </Text>
                                              </Tooltip>
                                            </Td>
                                          </Tr>
                                        )
                                      )}
                                    </Tbody>
                                  </Table>
                                </Box>
                              </Box>
                            )}

                            {docComparison.sourceFields.length > 0 &&
                              docComparison.interpretedFields.length > 0 && (
                                <Divider
                                  alignSelf="stretch"
                                  borderColor="compliance.debug.border"
                                  h="auto"
                                  orientation="vertical"
                                />
                              )}

                            {/* Interpreted Results */}
                            {docComparison.interpretedFields.length > 0 && (
                              <Box flex={1}>
                                <Text
                                  color="compliance.debug.text"
                                  fontSize="xs"
                                  fontWeight="medium"
                                  mb={2}
                                  opacity={0.7}
                                >
                                  Interpreted Results
                                </Text>
                                <Box maxH="200px" overflowY="auto" pr={2}>
                                  <Table size="sm" variant="simple">
                                    <Thead position="sticky" top={0} zIndex={1}>
                                      <Tr>
                                        <Th
                                          bg="compliance.debug.code.bg"
                                          color="compliance.debug.text"
                                          fontSize="xs"
                                          p={2}
                                        >
                                          Field Key
                                        </Th>
                                        <Th
                                          bg="compliance.debug.code.bg"
                                          color="compliance.debug.text"
                                          fontSize="xs"
                                          p={2}
                                        >
                                          Value
                                        </Th>
                                      </Tr>
                                    </Thead>
                                    <Tbody>
                                      {docComparison.interpretedFields.map(
                                        (field) => (
                                          <Tr key={field.fieldKey}>
                                            <Td fontSize="xs" p={2}>
                                              <Tooltip
                                                hasArrow
                                                bg="compliance.tooltip.bg"
                                                color="compliance.tooltip.text"
                                                label={field.fieldKey}
                                                placement="top"
                                              >
                                                <Text
                                                  color="compliance.debug.text"
                                                  fontWeight="medium"
                                                  maxW="150px"
                                                  noOfLines={1}
                                                >
                                                  {field.fieldKey}
                                                </Text>
                                              </Tooltip>
                                            </Td>
                                            <Td fontSize="xs" p={2}>
                                              <Tooltip
                                                hasArrow
                                                bg="compliance.tooltip.bg"
                                                color="compliance.tooltip.text"
                                                label={formatValue(field.value)}
                                                placement="top"
                                              >
                                                <Text
                                                  color={getCellColor(
                                                    field.value
                                                  )}
                                                  maxW="150px"
                                                  noOfLines={1}
                                                >
                                                  {formatValue(field.value)}
                                                </Text>
                                              </Tooltip>
                                            </Td>
                                          </Tr>
                                        )
                                      )}
                                    </Tbody>
                                  </Table>
                                </Box>
                              </Box>
                            )}
                          </HStack>
                        </Box>
                      </Box>
                    )}
                  </VStack>

                  {comparisonData.indexOf(docComparison) <
                    comparisonData.length - 1 && (
                    <Divider
                      borderColor="compliance.debug.border"
                      mt={4}
                      opacity={0.5}
                    />
                  )}
                </Box>
              ))}
            </VStack>
          )}
        </ModalBody>

        <ModalFooter>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
